<template>
    <div class="card">
        <header class="card-head">
            <div>
                <div class="card-title">{{ payload.title }}</div>
                <div v-if="payload.subtitle" class="card-sub">{{ payload.subtitle }}</div>
            </div>
            <div class="head-actions">
                <button
                    v-if="columns.length > columnLimit"
                    type="button"
                    class="chip"
                    @click="allColumns = !allColumns"
                >
                    {{ allColumns ? `Первые ${columnLimit}` : `Все ${columns.length}` }}
                </button>
                <button
                    v-if="showShare !== null"
                    type="button"
                    class="chip"
                    :class="{ 'chip--on': showShare }"
                    @click="showShare = !showShare"
                >
                    {{ showShare ? 'Скрыть доли' : 'Показать доли' }}
                </button>
            </div>
        </header>

        <!--
            Первая колонка залипает при горизонтальном скролле: без неё после
            третьего месяца непонятно, к какой марке относится строка.
        -->
        <div class="table-wrap">
            <table class="tbl">
                <thead>
                    <tr>
                        <th class="col-name sticky">{{ payload.rowLabel || 'Категория' }}</th>
                        <th v-for="col in visibleColumns" :key="col.id" class="num">{{ col.label }}</th>
                        <th class="num total-col">Итого</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in visibleRows" :key="row.name">
                        <td class="col-name sticky" :title="row.name">{{ row.name }}</td>
                        <td
                            v-for="(cell, idx) in row.visibleCells"
                            :key="idx"
                            class="num"
                        >
                            <span class="cell-value">{{ cell.value }}</span>
                            <span v-if="showShare && cell.share" class="cell-share">{{ cell.share }}</span>
                        </td>
                        <td class="num total-col">{{ row.total }}</td>
                    </tr>
                </tbody>
                <tfoot v-if="totals">
                    <tr>
                        <td class="col-name sticky">Итого</td>
                        <td v-for="(cell, idx) in totals.visibleCells" :key="idx" class="num">
                            <span class="cell-value">{{ cell.value }}</span>
                        </td>
                        <td class="num total-col">{{ totals.total }}</td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <button
            v-if="rows.length > rowLimit"
            type="button"
            class="toggle"
            @click="allRows = !allRows"
        >
            {{ allRows ? 'Свернуть' : `Показать ещё ${rows.length - rowLimit}` }}
        </button>

        <div v-if="payload.footer" class="footer">{{ payload.footer }}</div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    payload: { type: Object, required: true },
})

// 6 периодов — то, что читается без скролла на ноутбуке. Остальное по кнопке.
const columnLimit = 6
const rowLimit = 15
const allColumns = ref(false)
const allRows = ref(false)

const columns = computed(() => {
    const raw = Array.isArray(props.payload?.columns) ? props.payload.columns : []
    return raw
        .filter(col => col && (col.id || col.label))
        .map((col, idx) => ({ id: col.id || `c${idx}`, label: col.label || col.id || '' }))
})

const visibleColumnCount = computed(() => (allColumns.value ? columns.value.length : columnLimit))
const visibleColumns = computed(() => columns.value.slice(0, visibleColumnCount.value))

function formatNumber(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
    return value.toLocaleString('ru-RU')
}

function formatShare(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return ''
    return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
}

function normalizeCells(rawCells) {
    const cells = Array.isArray(rawCells) ? rawCells : []
    return columns.value.map((_, idx) => {
        const cell = cells[idx] || {}
        return {
            value: formatNumber(typeof cell.value === 'number' ? cell.value : null),
            share: formatShare(cell.share),
            numeric: typeof cell.value === 'number' ? cell.value : null,
        }
    })
}

const rows = computed(() => {
    const raw = Array.isArray(props.payload?.rows) ? props.payload.rows : []
    return raw.map(row => {
        const cells = normalizeCells(row?.cells)
        const total = typeof row?.total === 'number'
            ? row.total
            : cells.reduce((sum, cell) => sum + (cell.numeric ?? 0), 0)
        return { name: row?.name || '—', cells, total: formatNumber(total) }
    })
})

const visibleRows = computed(() => {
    const list = allRows.value ? rows.value : rows.value.slice(0, rowLimit)
    return list.map(row => ({ ...row, visibleCells: row.cells.slice(0, visibleColumnCount.value) }))
})

const totals = computed(() => {
    const raw = props.payload?.totals
    if (!Array.isArray(raw) || raw.length === 0) return null
    const cells = normalizeCells(raw)
    const total = cells.reduce((sum, cell) => sum + (cell.numeric ?? 0), 0)
    return {
        visibleCells: cells.slice(0, visibleColumnCount.value),
        total: formatNumber(total),
    }
})

// null = долей нет ни в одной ячейке, переключатель прятать целиком.
const showShare = ref(true)
if (!rows.value.some(row => row.cells.some(cell => cell.share))) showShare.value = null
</script>

<style scoped>
.card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 18px 20px;
    margin: 12px 0;
}

.card-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.card-title {
    font-size: 14px;
    font-weight: 700;
    color: #23282C;
    line-height: 1.3;
}

.card-sub {
    font-size: 11px;
    color: #888888;
    margin-top: 4px;
}

.head-actions {
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
}

.chip {
    background: transparent;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 5px 10px;
    font-family: inherit;
    font-size: 11px;
    color: #5f5f5f;
    cursor: pointer;
    white-space: nowrap;
}

.chip:hover { background: #f0f0f0; border-color: #c0c0c0; }
.chip--on { border-color: #E30613; color: #E30613; }

.table-wrap {
    width: 100%;
    overflow-x: auto;
}

.tbl {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12.5px;
}

.tbl th {
    text-align: right;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888888;
    padding: 8px 8px;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
    background: white;
}

.tbl td {
    padding: 8px;
    border-bottom: 1px solid #f0f0f0;
    color: #23282C;
    background: white;
}

.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.col-name {
    text-align: left;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    box-shadow: 1px 0 0 #f0f0f0;
}

.cell-value { display: block; }

.cell-share {
    display: block;
    font-size: 10px;
    color: #888888;
    margin-top: 2px;
}

.total-col {
    font-weight: 600;
    border-left: 1px solid #e7e7e7;
}

tfoot td {
    font-weight: 600;
    border-top: 1px solid #e0e0e0;
    border-bottom: none;
}

.toggle {
    margin-top: 10px;
    background: transparent;
    border: 1px solid #e0e0e0;
    padding: 7px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    color: #23282C;
    cursor: pointer;
}

.toggle:hover { background: #f0f0f0; border-color: #c0c0c0; }

.footer {
    font-size: 10.5px;
    color: #888888;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #e7e7e7;
}

@media (max-width: 600px) {
    .col-name { max-width: 110px; }
}
</style>
